'use client';

import type { ComponentType } from 'react';
import { useT } from '@/components/i18n-provider';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Handle, Position, useReactFlow, useViewport } from '@xyflow/react';
import { Bell, Maximize2, Minimize2, Minus, MoreHorizontal, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AdSlot } from '@/components/ads/ad-slot';
import { AppBar, type AppBarMenuRow } from '@/components/workspace/app-bar/app-bar';
import { identityVisual } from '@/components/workspace/rail/rail-dock';
import { useLiveContentMeta } from '@/components/workspace/item-label';
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect';
import {
  useDesktopFullscreen,
  useDesktopFullscreenShownId,
  useFullscreenItemId,
  useHandsetFullscreen,
  useOverviewMode,
  usePresentedWindowId,
  useTopAdWindowId,
  useWorkspaceActions,
  useWorkspaceItem,
} from '@/components/workspace/store';
import { isScreenType } from '@/lib/workspaces/screens';
import { announceWindowClosed } from '@/components/workspace/closed-window-undo';
import { useParticipantsMirror } from '@/components/workspace/presence/presence-bridge';
import {
  FullscreenChrome,
  useFullscreenChrome,
} from '@/components/workspace/fullscreen/fullscreen-chrome';
import { useDocumentFullscreen } from '@/components/workspace/fullscreen/use-document-fullscreen';
import { useCanvasCovered } from '@/components/workspace/canvas-cover';
import { openWorkspacePanel } from '@/components/workspace/panels/panel-events';
import { unreadBadgeText, useUnreadNotifications } from '@/components/workspace/unread-notifications';
import { getPlacement } from '@/lib/workspaces/types';
import { Tip } from '@/components/ui/tooltip';
import { useCoarsePointer } from '@/hooks/use-coarse-pointer';
import { useHandsetLandscape } from '@/hooks/use-handset-landscape';
import { useIsMd } from '@/components/workspace/panels/panel-shell';

export type DotTone = 'green' | 'amber' | 'purple' | 'blue' | 'rose' | 'slate';

export type NodeFrameAction = {
  id: string;
  label: string;
  tone: DotTone;
  /** Optional icon rendered inside the colored button. */
  icon?: ComponentType<{ className?: string }>;
  onClick: () => void;
  active?: boolean;
  /**
   * Overflow rows only: render the row dimmed and inert (click neither fires
   * `onClick` nor closes the menu). Use with a label that says WHY — an
   * enabled-looking row whose click silently does nothing reads as the action
   * failing. Header dots ignore this flag.
   */
  disabled?: boolean;
  /**
   * The row opens a surface drawn OUTSIDE this window's frame: a dialog
   * portaled straight to `<body>` (Remix, Drive, Publish, Propose to
   * original). While the window is the browser's full-screen element the
   * browser draws nothing outside it, so the desktop full-screen ⋯ leaves full
   * screen first and then runs the row; otherwise the dialog would open
   * unseen behind the app. Surfaces drawn inside the frame (Settings, the
   * version history) and `PopoverPortal` ones (Share) need no flag. Ignored
   * everywhere else.
   */
  leavesFullScreen?: boolean;
};

/**
 * macOS-style traffic-light palette — adapted to our app palette so the
 * three universal window actions read as a familiar trio:
 *  - close    → rose   (rightmost; destructive)
 *  - minimize → amber  (middle; "-")
 *  - maximize → green  (leftmost of the trio; full-screen)
 * Custom widget actions render to the LEFT of this trio.
 */
const TONE_BG: Record<DotTone, string> = {
  green: 'bg-emerald-400/90',
  amber: 'bg-amber-400/90',
  purple: 'bg-violet-400/90',
  blue: 'bg-sky-400/90',
  rose: 'bg-rose-400/90',
  slate: 'bg-slate-400/80',
};

const TONE_BG_HOVER: Record<DotTone, string> = {
  green: 'hover:bg-emerald-400',
  amber: 'hover:bg-amber-400',
  purple: 'hover:bg-violet-400',
  blue: 'hover:bg-sky-400',
  rose: 'hover:bg-rose-400',
  slate: 'hover:bg-slate-400',
};

const TONE_RING: Record<DotTone, string> = {
  green: 'ring-emerald-300/70',
  amber: 'ring-amber-300/70',
  purple: 'ring-violet-300/70',
  blue: 'ring-sky-300/70',
  rose: 'ring-rose-300/70',
  slate: 'ring-slate-300/70',
};

const DOT_BASE =
  'h-5 w-5 rounded-full transition-all outline-none flex items-center justify-center ' +
  // Subtle inner highlight + outer shadow paints the dot with a faint
  // glassy "bead" look. Hover scales up and reveals the icon glyph.
  'shadow-[inset_0_-1px_0_rgba(0,0,0,0.18),inset_0_1px_0_rgba(255,255,255,0.35),0_1px_1.5px_rgba(0,0,0,0.28)]';

/** One header action rendered as a coloured glassy dot with a tooltip. */
function ActionDot({ action: a }: { action: NodeFrameAction }) {
  const Icon = a.icon;
  return (
    <Tip label={a.label}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          a.onClick();
        }}
        aria-label={a.label}
        aria-pressed={a.active}
        className={cn(
          DOT_BASE,
          TONE_BG[a.tone],
          TONE_BG_HOVER[a.tone],
          'hover:scale-110',
          a.active && cn('ring-2 ring-offset-0', TONE_RING[a.tone]),
        )}
      >
        {Icon && <Icon className="h-2.5 w-2.5 text-black/60" />}
      </button>
    </Tip>
  );
}

/**
 * Overview cover — mounted over a screen's body while the camera is
 * pulled back to the overview. Blocks app interaction (tiny iframes are
 * unclickable targets anyway), doubles as a drag handle so screens can
 * be rearranged from the overview, and a click with no movement flies
 * into the screen. The name chip counter-scales by 1/zoom so it reads
 * at a constant screen size no matter how far the camera is pulled back
 * (same trick as the comment pins).
 */
export function OverviewCover({
  itemId,
  title,
  hideTitle = false,
}: {
  itemId: string;
  title: string;
  /**
   * Blank-screen sheets label themselves in the overview's under-cell
   * label row (Part VI) — the centered chip would duplicate it. The
   * cover stays as the click-to-enter / drag surface; peers still show.
   */
  hideTitle?: boolean;
}) {
  const { zoom } = useViewport();
  const downRef = useRef<{ x: number; y: number } | null>(null);
  const scale = Math.min(1 / Math.max(zoom, 0.02), 12);
  // Peers parked on this screen — small colored dots beside the name.
  const peers = useParticipantsMirror().filter(
    (p) => p.screenItemId === itemId,
  );
  return (
    <div
      className="ws-node-drag-handle absolute inset-0 z-30 flex cursor-grab items-center justify-center rounded-2xl transition-shadow hover:bg-foreground/[0.03] hover:ring-2 hover:ring-primary/50 active:cursor-grabbing"
      role="button"
      aria-label={`Open ${title}`}
      onPointerDown={(e) => {
        downRef.current = { x: e.clientX, y: e.clientY };
      }}
      onClick={(e) => {
        const d = downRef.current;
        downRef.current = null;
        // A drag that ended here still synthesizes a click — only a
        // press with no real movement counts as "enter this screen".
        if (d && Math.hypot(e.clientX - d.x, e.clientY - d.y) > 5) return;
        e.stopPropagation();
        window.dispatchEvent(
          new CustomEvent('ws:screen', { detail: { kind: 'item', itemId } }),
        );
      }}
    >
      {(!hideTitle || peers.length > 0) && (
        <span
          className="pointer-events-none flex max-w-[80%] items-center gap-1.5 rounded-full border border-border/60 bg-background/95 px-3 py-1 text-xs font-medium text-foreground shadow-sm"
          style={{ transform: `scale(${scale})` }}
        >
          {!hideTitle && <span className="truncate">{title}</span>}
          {peers.length > 0 && (
            <span aria-hidden className="flex shrink-0 -space-x-1">
              {peers.slice(0, 3).map((p) => (
                <span
                  key={p.user.peerId}
                  className="flex h-3.5 w-3.5 items-center justify-center rounded-full text-[8px] font-semibold text-white ring-1 ring-background"
                  style={{ backgroundColor: p.user.color }}
                >
                  {(p.user.username || '?').slice(0, 1).toUpperCase()}
                </span>
              ))}
            </span>
          )}
        </span>
      )}
    </div>
  );
}

/**
 * Green-control hover picker (Snap-Layouts style): hovering the
 * maximize dot for a beat offers Left half / Fill screen / Right half.
 * The tiles dispatch `ws:screen {kind:'layout'}` — the canvas places
 * the window in that region of the current view, and two windows tiled
 * into complementary halves settle/frame as ONE screenful. Hanging the
 * picker on a control users already touch is the whole discoverability
 * trick; clicking the dot keeps its normal fullscreen behavior.
 */
function LayoutPickerDot({
  action,
  itemId,
}: {
  action: NodeFrameAction;
  itemId: string;
}) {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enter = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(true), 380);
  };
  const leave = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    setOpen(false);
  };
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  const pick = (layout: 'left' | 'full' | 'right') => {
    setOpen(false);
    window.dispatchEvent(
      new CustomEvent('ws:screen', {
        detail: { kind: 'layout', itemId, layout },
      }),
    );
  };
  const tile = (layout: 'left' | 'full' | 'right', label: string) => (
    <Tip label={label}>
      <button
        type="button"
        aria-label={label}
        onClick={(e) => {
          e.stopPropagation();
          pick(layout);
        }}
        className="flex h-6 w-9 items-center justify-center rounded-md border border-border/60 p-[3px] transition-colors hover:border-primary/60 hover:bg-accent/50"
      >
        <span className="flex h-full w-full gap-[2px]">
          {layout === 'left' && (
            <>
              <span className="h-full flex-1 rounded-[2px] bg-foreground/55" />
              <span className="h-full flex-1 rounded-[2px] bg-foreground/15" />
            </>
          )}
          {layout === 'full' && (
            <span className="h-full w-full rounded-[2px] bg-foreground/55" />
          )}
          {layout === 'right' && (
            <>
              <span className="h-full flex-1 rounded-[2px] bg-foreground/15" />
              <span className="h-full flex-1 rounded-[2px] bg-foreground/55" />
            </>
          )}
        </span>
      </button>
    </Tip>
  );
  return (
    <div className="relative" onMouseEnter={enter} onMouseLeave={leave}>
      <ActionDot action={action} />
      {open && (
        // The pt bridge keeps the pointer inside the hover region while
        // crossing from the dot to the tiles (no flicker gap).
        <div className="absolute right-0 top-full z-30 pt-1.5">
          <div className="flex items-center gap-1 rounded-xl border border-border/60 bg-popover p-1.5 shadow-lg">
            {tile('left', 'Left half')}
            {tile('full', 'Fill screen')}
            {tile('right', 'Right half')}
          </div>
        </div>
      )}
    </div>
  );
}

/** A single slate `⋯` dot opening a popover of secondary actions. */
function OverflowMenu({ items }: { items: NodeFrameAction[] }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <Tip label={t('workspace.node.more')}>
        <button
          type="button"
          aria-label={t('workspace.toolbar.moreActions')}
          aria-expanded={open}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          className={cn(
            DOT_BASE,
            TONE_BG.slate,
            TONE_BG_HOVER.slate,
            'hover:scale-110',
            open && cn('ring-2 ring-offset-0', TONE_RING.slate),
          )}
        >
          <MoreHorizontal className="h-2.5 w-2.5 text-black/60" />
        </button>
      </Tip>
      {open && (
        <>
          {/* Click-away */}
          <div aria-hidden className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            role="menu"
            className="glass-panel absolute right-0 top-full mt-1 z-50 w-48 rounded-lg py-1 shadow-2xl nodrag nopan"
          >
            {items.map((a) => {
              const Icon = a.icon;
              return (
                <button
                  key={a.id}
                  type="button"
                  role="menuitem"
                  aria-disabled={a.disabled || undefined}
                  onClick={(e) => {
                    e.stopPropagation();
                    // A disabled row keeps the menu OPEN (standard menu
                    // behaviour) so its label stays readable and another row
                    // can be picked — closing made the click look like a
                    // silently failed action.
                    if (a.disabled) return;
                    setOpen(false);
                    a.onClick();
                  }}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors',
                    a.disabled
                      ? 'cursor-default text-foreground/45'
                      : cn(
                          'hover:bg-foreground/8',
                          a.active ? 'text-foreground font-medium' : 'text-foreground/80',
                        ),
                  )}
                >
                  {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                  <span className="truncate">{a.label}</span>
                  {a.active && (
                    <span className="ml-auto h-1.5 w-1.5 rounded-full bg-sky-500 shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export function NodeFrame({
  itemId,
  title,
  subtitle,
  actions,
  overflowActions,
  headerAccessory,
  cornerBadge,
  showCloseDot = true,
  onMinimize,
  onMaximize,
  onMaximizeOverride,
  maximizeOverrideActive = false,
  onFocus,
  onCornerBadgeClick,
  cornerBadgeAriaLabel,
  contentClassName,
  children,
  bodyPad = true,
  minWidth = 220,
  minHeight = 160,
  maxWidth = 1600,
  maxHeight = 1600,
  collapsed = false,
  adAppId = null,
  accessoryActions,
  fullScreenActions,
}: {
  itemId: string;
  /**
   * The header accessory's verbs as menu rows (Remix, Drive), for the phone
   * app bar, whose ⋯ sheet holds them (wireframes v3, section A: "Remix and
   * Drive, folded in from the app bar, first in the sheet as they were first
   * on the bar"). `headerAccessory` is opaque JSX and cannot be folded, so a
   * node that renders pills passes their actions here too. A windowed
   * desktop ignores it (the pills stay in the title bar); the desktop
   * full-screen app bar folds them into its ⋯ the same way (lane B).
   */
  accessoryActions?: NodeFrameAction[];
  /**
   * Title-bar pills that are not in `accessoryActions`, as rows for the
   * DESKTOP full-screen app bar only, first in its ⋯ (lane B: "every action
   * the title bar offered stays reachable"). Today that is the app's Update
   * pill while a new version is waiting. The phone sheet does not read it,
   * so lane C's rows are unchanged.
   */
  fullScreenActions?: NodeFrameAction[];
  /**
   * The app shown in this window (its `widgetContentId`), for the title-bar
   * ad slot (ads build plan section 2.4). Null or absent means this window
   * never carries an ad: sign-in, payment, error and empty screens, chats,
   * plain files. Only the content node passes it, and only for a resolved
   * app. The slot itself is drawn in every header as the free middle, empty
   * unless this is set AND this window is the device's top app window.
   */
  adAppId?: string | null;
  /**
   * Optional handler fired when the user double-clicks / double-taps
   * the widget header. Node implementations use it to bring the widget
   * into view without sending it to fullscreen: content windows
   * maximize to the viewport, agents and posts dispatch `ws:focus-item`.
   * Never a zoom (workspace zoom is disabled; agents and posts called
   * `fitView` here until 2026-09-26). The gesture is intentionally
   * scoped to the header only (never the body) so double-clicking
   * inside a chat input / editor doesn't surprise the user.
   */
  onFocus?: () => void;
  /** Widget title — the bold line in the window chrome. */
  title: React.ReactNode;
  /**
   * Optional secondary line under the title (e.g. `@username` for an
   * agent widget). Renders in the muted/violet style depending on
   * caller — we accept ReactNode so callers can colour it themselves.
   */
  subtitle?: React.ReactNode;
  /**
   * Widget-specific action buttons. Rendered on the right side of the
   * header, ordered LEFT → RIGHT in the array, followed by the three
   * universal window controls (`maximize / minimize / close`) in that
   * fixed order — so the trio is always anchored to the right edge.
   */
  actions?: NodeFrameAction[];
  /**
   * Secondary actions collapsed behind a single `⋯` dot (rendered just
   * before the window controls). Keeps the header lean — only the most
   * relevant inline `actions` stay visible; everything else lives in
   * this popover menu (icon + label rows). Generic, so any node type
   * can adopt it.
   */
  overflowActions?: NodeFrameAction[];
  /**
   * Optional promoted element rendered in the header BETWEEN the title
   * column and the action-dot strip — used for a prominent pill CTA
   * (e.g. the widget "Remix" gradient button) that shouldn't be reduced
   * to a coloured dot. Stays `shrink-0`; callers are responsible for
   * collapsing it to icon-only on narrow widgets.
   */
  headerAccessory?: React.ReactNode;
  /**
   * Optional element rendered overlapping the top-left corner of the
   * frame — typically a large agent avatar. The badge straddles the
   * top edge so it reads as the visual anchor of the widget, sketch-
   * mockup style. When `onCornerBadgeClick` is provided the badge is
   * wrapped in a button so users can tap it to collapse / expand.
   */
  cornerBadge?: React.ReactNode;
  /** Append the universal close dot. Defaults true. */
  showCloseDot?: boolean;
  /** If provided, exposes the "-" minimize button (amber). */
  onMinimize?: () => void;
  /** If provided, exposes the maximize button (green). */
  onMaximize?: () => void;
  /**
   * If provided, the green maximize dot calls THIS instead of the in-canvas
   * fullscreen takeover. App/widget nodes use it to present the node with the
   * native Fullscreen API in place (2026-09-21; the portal takeover remounts
   * an iframe), falling back to the standalone `/w` page when fullscreen is
   * refused. Takes precedence over `onMaximize`.
   */
  onMaximizeOverride?: () => void;
  /**
   * True while the override's own presentation is up (the node is the
   * document's fullscreen element). The dot then reads as the way back and
   * calls the override again, which exits.
   */
  maximizeOverrideActive?: boolean;
  /**
   * If provided, the corner badge becomes a button — clicking it
   * triggers this callback (typically to collapse/expand the widget).
   */
  onCornerBadgeClick?: () => void;
  /** Aria-label for the corner badge button. */
  cornerBadgeAriaLabel?: string;
  contentClassName?: string;
  children: React.ReactNode;
  bodyPad?: boolean;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  /**
   * Collapsed "puck" mode — the frame fades out, the header
   * compresses, and only the corner badge + title/subtitle stack
   * remain centred in the node bounds. Combined with a width/height
   * patch on the workspace item this gives the avatar-puck shrink
   * effect from the sketch.
   */
  collapsed?: boolean;
}) {
  const t = useT();
  const { removeItem, bringToFront, patchItemState, setFullscreenItem, setHandsetFullscreen } =
    useWorkspaceActions();
  // Touch: the header's double-tap fills the screen (see onDoubleClick).
  const coarseHeaderGestures = useCoarsePointer();
  // NO MAXIMIZE ON A HANDSET (D-3 a of the Revent mobile first-touch design,
  // docs/internal/architecture/revent-mobile-first-touch.md). The canvas
  // already frames a window at 100 percent on a handset, so the green dot
  // could only present a phone-shaped window fullscreen or navigate to the
  // standalone page, where an app like Revent runs as a device-only share view.
  // Same definition of a handset as the canvas's own framing
  // (`!isMdViewport || handsetLandscape`, workspace-canvas.tsx). The dot stays
  // while it is the way BACK from a fullscreen the person is already in.
  const isMdViewport = useIsMd();
  const handsetLandscape = useHandsetLandscape();
  const handset = !isMdViewport || handsetLandscape;
  // THE PHONE APP VIEW (handoff 2026-09-29, section 5; wireframes v3,
  // section A). On a handset this frame is presented full-tab by the canvas
  // and its header is the shared 44 px glass APP BAR (app-bar.tsx): the
  // app's icon and name, the ad slot and ⋯, nothing else. Not a title bar:
  // no traffic lights, no drag handle, no double-tap, no long press, no
  // resize edges; everything the window buttons and the window menu did is
  // a 44 px row in the ⋯ sheet. `mounted` is the same pre-paint gate the
  // bottom bar and the rail use: `useIsMd` starts false, so without it a
  // laptop would paint the phone bar for its first frame.
  const [mounted, setMounted] = useState(false);
  useIsomorphicLayoutEffect(() => setMounted(true), []);
  const handsetUi = mounted && handset;
  const presentedId = usePresentedWindowId();
  const handsetFullscreen = useHandsetFullscreen();
  // The person's unread notifications, as the rail holds them; null when
  // there is no one to notify (unread-notifications.ts). Read by the bars'
  // ⋯ below, see NOTIFICATIONS IN FULL SCREEN.
  const unread = useUnreadNotifications();
  const live = useLiveContentMeta();
  // A collapsed agent puck has no header to reach on a phone; presented
  // full-tab it is drawn whole, with the bar, so it can be used and closed.
  const collapsedUi = collapsed && !handsetUi;
  // Subscribe to only this widget's item — see commentary on the same
  // pattern in `useNodeResize`. Subscribing to the full items array
  // would re-render every widget on every patch, killing resize +
  // drag performance on workspaces with more than a handful of
  // widgets.
  const item = useWorkspaceItem(itemId);
  const itemRef = useRef(item);
  itemRef.current = item;
  const flow = useReactFlow();
  const fullscreenItemId = useFullscreenItemId();
  const isFullscreen = fullscreenItemId === itemId;
  // Overview covers only screens (app-like windows) — board content
  // (notes, shapes) stays bare in the pulled-back view.
  const overviewMode = useOverviewMode();
  const showOverviewCover =
    overviewMode && !isFullscreen && !!item && isScreenType(item.type);
  // DESKTOP FULL SCREEN (lane B, 2026-09-29; wireframes v3, section B). A
  // window is full screen on a desktop in two ways: the browser presents its
  // node (the green dot and the window menu's "Open full screen",
  // `maximizeOverrideActive`), or the in-canvas takeover portals its frame
  // over the page (`isFullscreen`, still reached by non-app content and the
  // `?fullscreen=` link). Either way the header is the always-visible glass
  // APP BAR (placement `fullScreen`), the phone's bar drawn wider: the app's
  // icon and name, the ad and ⋯, nothing else. No title bar, no traffic
  // lights, no drag handle, no resize edges, no double-click; ⋯ holds every
  // action the title bar offered. The app's box starts under the bar. The
  // takeover's hidden pull-down (FullscreenChrome) is retired here; a PHONE
  // takeover keeps it (lane C's path is untouched) and so does the `/w/`
  // share page on the apex.
  //
  // LANE D (2026-09-29; the Stage algorithm, rules 12 to 17): the green dot's
  // full screen is no longer the browser presenting this node. The canvas
  // draws the node over the whole canvas at a display-only size, on a
  // temporary screen of this device, while the PAGE is in the browser's full
  // screen; `maximizeOverrideActive` now means "this window is full screen
  // on this device" (content-node reads the store's `desktopFullscreen`).
  // The app bar stays the header also while the person looks at another
  // screen (the window is hidden there, and keeping its layout means going
  // back does not reflow the app); `desktopFullscreenShownId` names the one
  // being SHOWN, which alone may carry the ad.
  const desktopFullScreen = !handsetUi && (isFullscreen || maximizeOverrideActive);
  const desktopFullscreenShownId = useDesktopFullscreenShownId();
  const desktopFullscreenShown = !handset && !!desktopFullscreenShownId;
  // A window this device shows full screen whose temporary screen is not the
  // one on view: the canvas hides it, so its app bar must carry no ad.
  const inDeviceFullscreen = useDesktopFullscreen().some((r) => r.id === itemId);
  const desktopFullscreenAway = inDeviceFullscreen && desktopFullscreenShownId !== itemId;
  // Anything full screen over the page on this device: the takeover, an
  // element the browser shows full screen (see use-document-fullscreen.ts),
  // or a window this device shows full screen (lane D).
  const documentFullscreen = useDocumentFullscreen();
  const anyFullScreen = !!fullscreenItemId || documentFullscreen || desktopFullscreenShown;
  // ONE AD PER DEVICE (ads build plan section 2.4): the title-bar slot is
  // active only on the top app window of the current screen, and never in
  // either full-screen mode, in the overview, on a handset (lanes B and C
  // move it to the app bar there) or on a collapsed puck. If that window
  // lacks the room the slot stays empty and no other window takes the ad.
  // While ANY window is full screen no title bar carries one (lane B): the
  // full-screen app bar is the device's slot then, and a title bar under the
  // full-screen app would still count as seen.
  //
  // NOTHING UNDER A COVER (2026-09-30 fix round): an ad sat "shown" under the
  // Admin dialog and counted. `canvasCovered` (canvas-cover.ts) is true while
  // a workspace dialog, Discover over the canvas, Stage (the overview, the
  // phone's Stage sheet) or, below md, an edge-panel sheet covers the canvas;
  // then no placement this frame draws is live, so none makes a request.
  // components/ads/ad-trackers.ts still hit-tests the ad before counting
  // anything, for whatever else lies over it.
  const canvasCovered = useCanvasCovered({ narrow: !isMdViewport, overview: overviewMode });
  const topAdWindowId = useTopAdWindowId();
  const adActive =
    !!adAppId &&
    topAdWindowId === itemId &&
    !isFullscreen &&
    !maximizeOverrideActive &&
    !anyFullScreen &&
    !canvasCovered &&
    !handset &&
    !collapsed;
  // The app bar's slot (placement 'appBar') is live for the ONE window the
  // handset presents; the others are hidden behind it and draw nothing.
  const appBarAdActive = handsetUi && !!adAppId && presentedId === itemId && !isFullscreen && !maximizeOverrideActive && !canvasCovered;
  // The desktop full-screen bar's slot (placement 'fullScreen') is live for
  // the window that IS full screen: the one this device shows (lane D; with
  // several full screen at once, only the one on view, rule 17), or the
  // takeover while no element covers it.
  const fullScreenAdActive =
    desktopFullScreen &&
    !!adAppId &&
    !desktopFullscreenAway &&
    (maximizeOverrideActive || !documentFullscreen) &&
    !canvasCovered;
  // Fullscreen header reveal: Esc-hold / top-edge hover / touch grab
  // handle pull the app header back in as an overlay. See
  // fullscreen-chrome.tsx for the gesture model. A PHONE takeover only: a
  // desktop full screen has its app bar up at all times.
  const fsChrome = useFullscreenChrome({ active: isFullscreen && handsetUi });

  type ResizeAnchor = 't' | 'l' | 'r' | 'b' | 'tl' | 'tr' | 'bl' | 'br';
  const resizeRef = useRef<{
    pointerId: number;
    anchor: ResizeAnchor;
    startClientX: number;
    startClientY: number;
    startX: number;
    startY: number;
    startW: number;
    startH: number;
  } | null>(null);

  const beginResize = useCallback(
    (e: React.PointerEvent, anchor: ResizeAnchor) => {
      // Reject anything other than primary mouse / pen / touch
      // contact: right-click on a resize edge should fall through to
      // the workspace context menu, not start a resize the user
      // never asked for.
      if (e.button !== 0) return;
      // Desktop full screen in place (lane B): the node fills the screen,
      // so its edge strips sit on the screen's edges, and a press there
      // would resize the STORED window for everyone. The strips are hidden
      // there too (`:fullscreen .ws-resize-edge` in globals.css).
      if (maximizeOverrideActive) return;
      e.preventDefault();
      e.stopPropagation();
      const it = itemRef.current;
      if (!it) return;
      try {
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      } catch {
        // No active pointer to capture (synthetic events, odd pen
        // states) — the zone's own move/up handlers still drive the
        // gesture while the pointer stays over it.
      }
      bringToFront(itemId);
      resizeRef.current = {
        pointerId: e.pointerId,
        anchor,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: it.state.x,
        startY: it.state.y,
        startW: it.state.width,
        startH: it.state.height,
      };
    },
    [itemId, bringToFront, maximizeOverrideActive],
  );

  // Aspect-ratio lock for pasted media. When `state.cache.aspectRatio`
  // is set (the image / video onLoad handler records it on first
  // paint — see `content-node.tsx::onNaturalSize`), the resize
  // gesture preserves that ratio so the picture never gets
  // squished. Headerless flag gates this so a user who explicitly
  // re-enables the chrome title can still freely reshape the frame.
  const lockAspect =
    item?.state?.hideHeader && typeof item.state.cache?.aspectRatio === 'number'
      ? (item.state.cache.aspectRatio as number)
      : null;

  const onResizeMove = useCallback(
    (e: React.PointerEvent) => {
      const start = resizeRef.current;
      if (!start) return;
      const z = flow.getZoom() || 1;
      // Pointer deltas are in screen pixels; divide by zoom to convert
      // into flow-space units so the card tracks the cursor 1:1.
      const dx = (e.clientX - start.startClientX) / z;
      const dy = (e.clientY - start.startClientY) / z;
      // Which edges this anchor moves. The OPPOSITE edge stays pinned —
      // x/y are re-derived from the final width/height at the end, so
      // clamping and grid snapping can never let it drift.
      const hasL =
        start.anchor === 'l' || start.anchor === 'tl' || start.anchor === 'bl';
      const hasR =
        start.anchor === 'r' || start.anchor === 'tr' || start.anchor === 'br';
      const hasT =
        start.anchor === 't' || start.anchor === 'tl' || start.anchor === 'tr';
      const hasB =
        start.anchor === 'b' || start.anchor === 'bl' || start.anchor === 'br';
      let nextW = start.startW;
      let nextH = start.startH;
      if (hasR) nextW = start.startW + dx;
      if (hasL) nextW = start.startW - dx;
      if (hasB) nextH = start.startH + dy;
      if (hasT) nextH = start.startH - dy;

      // Lock-aspect pass. Side anchors drive a single axis and we
      // derive the other from the ratio; corners lead with width.
      if (lockAspect && lockAspect > 0) {
        if (hasL || hasR) {
          // Width is the leader; height follows.
          nextH = nextW / lockAspect;
        } else if (hasB || hasT) {
          // Pure top/bottom edge: height leads; width follows.
          nextW = nextH * lockAspect;
        }
      }

      // ONLY THE AXES BEING DRAGGED CHANGE, AND ONLY THEY SNAP (fix round 2,
      // 2026-09-30). The clamp and the 24 px snap below ran on BOTH axes for
      // every anchor, so a purely horizontal drag of a window's right edge
      // also moved its height from 850 to 840, and a window the Stage cascade
      // placed at 836 or 820 px tall (the 12 px steps are not on the grid)
      // lost its stick-out the first time its width was touched. An axis the
      // anchor does not move keeps its start value exactly, whatever the
      // bounds or the grid say. An aspect-locked picture is the one
      // exception: there the ratio is the rule, and the other axis follows
      // it, as it always has.
      const locked = !!lockAspect && lockAspect > 0;
      const movesW = hasL || hasR || locked;
      const movesH = hasT || hasB || locked;
      let clampedW = movesW ? Math.max(minWidth, Math.min(maxWidth, nextW)) : start.startW;
      let clampedH = movesH ? Math.max(minHeight, Math.min(maxHeight, nextH)) : start.startH;
      // Aspect lock + min/max clamp interaction. The naive
      // `max(min, …)` on each axis breaks the ratio when one
      // dimension hits a wall before the other (e.g. a 2 : 1
      // picture shrunk to `minWidth = minHeight = 48` ends up
      // square because height hits 48 while width is still 48).
      // We re-derive the pinned axis from the locked ratio after
      // each per-axis clamp so the picture stays in proportion
      // and the effective floor is `(min × ratio, min)` for
      // landscape or `(min, min / ratio)` for portrait, whichever
      // produces a result inside both walls.
      if (lockAspect && lockAspect > 0) {
        if (hasL || hasR) {
          clampedH = clampedW / lockAspect;
          if (clampedH < minHeight) {
            clampedH = minHeight;
            clampedW = clampedH * lockAspect;
          }
        } else if (hasB || hasT) {
          clampedW = clampedH * lockAspect;
          if (clampedW < minWidth) {
            clampedW = minWidth;
            clampedH = clampedW / lockAspect;
          }
        }
      }
      // Snap edges to the visible 24px lattice so resize clicks to the
      // same gridlines that drag does. We snap the moving edge only —
      // the opposite edge stays pinned via the final x/y derivation
      // below. For aspect-locked nodes we snap on width only and
      // re-derive height from the ratio, so a snapped width never
      // desyncs the locked pair.
      const snapStep = 24;
      const snappedW = movesW ? Math.round(clampedW / snapStep) * snapStep : clampedW;
      let snappedH = movesH ? Math.round(clampedH / snapStep) * snapStep : clampedH;
      if (lockAspect && lockAspect > 0) {
        snappedH = Math.round(snappedW / lockAspect);
      }
      clampedW = movesW ? Math.max(minWidth, Math.min(maxWidth, snappedW)) : start.startW;
      clampedH = movesH ? Math.max(minHeight, Math.min(maxHeight, snappedH)) : start.startH;
      // Final aspect-lock pass after the snap-grid quantisation
      // and per-axis clamp. Same logic as above — when the snap
      // / clamp drags one axis below the floor we re-derive the
      // other from the ratio rather than leaving the picture
      // squished. This is the path users actually hit at the
      // minimum: at 48 × 48 with a 2 : 1 ratio the naive clamp
      // would lock the node square, this restores 96 × 48.
      if (lockAspect && lockAspect > 0) {
        if (hasL || hasR) {
          const derivedH = Math.round(clampedW / lockAspect);
          if (derivedH < minHeight) {
            clampedH = minHeight;
            clampedW = Math.round(minHeight * lockAspect);
          } else {
            clampedH = derivedH;
          }
        } else if (hasB || hasT) {
          const derivedW = Math.round(clampedH * lockAspect);
          if (derivedW < minWidth) {
            clampedW = minWidth;
            clampedH = Math.round(minWidth / lockAspect);
          } else {
            clampedW = derivedW;
          }
        }
      }
      // Pin the opposite edge exactly: left/top handles re-derive x/y
      // from the FINAL size, so no clamp/snap path can drift the
      // right/bottom edge (and vice versa the window's own origin
      // stays put for right/bottom handles).
      patchItemState(
        itemId,
        {
          x: hasL ? start.startX + start.startW - clampedW : start.startX,
          y: hasT ? start.startY + start.startH - clampedH : start.startY,
          width: clampedW,
          height: clampedH,
        },
        { persist: false },
      );
    },
    [
      flow,
      patchItemState,
      itemId,
      minWidth,
      minHeight,
      maxWidth,
      maxHeight,
      lockAspect,
    ],
  );

  // Esc cancels an in-flight header-frame resize: restore the gesture's
  // start rect and drop the gesture state. The listener lives for the
  // node's lifetime but is a ref-check no-op unless THIS node owns an
  // active gesture. Capture phase + preventDefault so the cancel wins
  // over other Esc consumers (panel close, tool disarm). Subsequent
  // pointermove/up frames no-op via the cleared `resizeRef`.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const start = resizeRef.current;
      if (!start || e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      resizeRef.current = null;
      patchItemState(
        itemId,
        { x: start.startX, y: start.startY, width: start.startW, height: start.startH },
        { debounceMs: 200 },
      );
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [itemId, patchItemState]);

  const endResize = useCallback(
    (e: React.PointerEvent) => {
      if (!resizeRef.current) return;
      try {
        (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
      } catch {
        // ignore
      }
      const it = itemRef.current;
      resizeRef.current = null;
      if (it) {
        // Final patch with persist (debounced) so the server picks up
        // the terminal dimensions. Pass the current values explicitly so
        // we don't depend on a closure capturing the latest store state.
        patchItemState(
          itemId,
          { x: it.state.x, y: it.state.y, width: it.state.width, height: it.state.height },
          { debounceMs: 200 },
        );
      }
    },
    [itemId, patchItemState],
  );

  // Compose the action strip in canonical order:
  //   1. Custom widget actions (… leftmost)
  //   2. Maximize (green)  ← present if onMaximize given
  //   3. Minimize (amber)  ← present if onMinimize given
  //   4. Close    (rose)   ← present unless showCloseDot is false
  // This mirrors macOS traffic-light grouping but flipped to the right
  // edge of the window per our convention.
  //
  // `onMaximize` semantics: send the widget to FULLSCREEN takeover —
  // hides every other piece of workspace chrome and lets the widget
  // own the viewport. (The earlier "zoom-to-fit" behaviour now lives
  // on `onFocus`, triggered by double-clicking the header.) Callers
  // can still opt out of the green dot entirely by leaving onMaximize
  // undefined.
  const enterFullscreen = useCallback(() => {
    setFullscreenItem(itemId);
    if (onMaximize) onMaximize();
  }, [setFullscreenItem, itemId, onMaximize]);
  const exitFullscreen = useCallback(() => {
    setFullscreenItem(null);
  }, [setFullscreenItem]);
  const trailing: NodeFrameAction[] = [];
  if (isFullscreen && (onMaximize || onMaximizeOverride)) {
    // Already in the in-canvas takeover: the only useful green-dot action is
    // the way back. An app node's override would otherwise navigate the
    // person away to the standalone page instead of returning the canvas,
    // and the rail chords are muted while a node is fullscreen, so that
    // would leave no way out at all.
    trailing.push({
      id: '__maximize__',
      label: t('content.exitFullscreen'),
      tone: 'green' as const,
      icon: Minimize2,
      onClick: exitFullscreen,
    });
  } else if (handset && !maximizeOverrideActive) {
    // A handset gets no maximize control; see the note at the top.
  } else if (onMaximizeOverride) {
    // App nodes: the green dot presents the node fullscreen in place (or
    // opens `/w` when fullscreen is refused) rather than the in-canvas takeover.
    // While that presentation is up the same dot is the exit. Its label is the
    // window menu's own "Open full screen" row (fix round 2: it was a literal
    // "Open fullscreen", one of three spellings a person met for one act).
    trailing.push({
      id: '__maximize__',
      label: maximizeOverrideActive ? t('content.exitFullscreen') : t('workspace.node.openFullScreen'),
      tone: 'green' as const,
      icon: maximizeOverrideActive ? Minimize2 : Maximize2,
      onClick: onMaximizeOverride,
    });
  } else if (onMaximize) {
    trailing.push({
      id: '__maximize__',
      label: isFullscreen ? t('content.exitFullscreen') : t('workspace.node.maximize'),
      tone: 'green' as const,
      icon: isFullscreen ? Minimize2 : Maximize2,
      onClick: isFullscreen ? exitFullscreen : enterFullscreen,
    });
  }
  if (onMinimize) {
    trailing.push({
      id: '__minimize__',
      label: 'Minimize',
      tone: 'amber' as const,
      icon: Minus,
      onClick: onMinimize,
    });
  } else if (showCloseDot) {
    // Universal "minimize to dock" (amber) — parks the app in the rail
    // dock (`placement: 'docked'`): the canvas instance disappears and a
    // dock tile restores it on click. Sits between maximize (green) and
    // close (red) so the trio reads close · minimize · maximize from the
    // right edge, matching the macOS traffic-light grouping. Nodes that
    // own a different minimize gesture (e.g. an agent collapsing to a
    // puck) supply `onMinimize` above and opt out of this default.
    trailing.push({
      id: '__dock__',
      label: 'Minimize to dock',
      tone: 'amber' as const,
      icon: Minus,
      onClick: () => patchItemState(itemId, { placement: 'docked' }),
    });
  }
  // Widgets pinned to the dock (`placement: 'pinned'`) shouldn't be
  // destroyed by the close dot — that would discard the pin the user
  // just set up. Instead, minimize them: the dock tile stays, the
  // canvas instance disappears, and clicking the tile restores it.
  // Tooltip switches to Send to dock so the action reads honest.
  const pinnedToDock = item ? getPlacement(item.state) === 'pinned' : false;
  const closeWindow = () => {
    if (pinnedToDock) {
      patchItemState(itemId, { placement: 'docked' });
    } else {
      // Say what is about to go BEFORE it goes, with the whole item, so
      // the close toast can put it back as it was (closed-window-undo.tsx).
      // No confirm: closing keeps the app's content, so it is reversible,
      // and Undo is the house pattern for a reversible act (the Revent
      // e2e run of 2026-09-26/27 closed a window from the keyboard, with
      // focus on this dot, and had no way back).
      if (item) announceWindowClosed(item);
      removeItem(itemId);
    }
  };
  if (showCloseDot) {
    trailing.push({
      id: '__close__',
      label: pinnedToDock ? t('workspace.node.sendToDock') : t('workspace.canvas.node.close'),
      tone: 'rose' as const,
      icon: X,
      onClick: closeWindow,
    });
  }
  const titleText =
    typeof title === 'string' || typeof title === 'number'
      ? String(title)
      : 'Widget';

  // THE ⋯ SHEET on a phone holds everything the window buttons and the
  // window menu did on a desktop (wireframes v3, section A): Remix and Drive
  // where they apply, Full screen (or Exit full screen), then the node's own
  // window-menu rows (Share, Settings, the publishing rows, the view
  // toggle), its inline actions, Minimize and Close. Full screen here is the
  // IN-TAB flag (the navigation steps aside, the bar stays), never the
  // browser's Fullscreen API and never a geometry write; Minimize and Close
  // leave full screen first, as on a desktop.
  const toRow = (a: NodeFrameAction): AppBarMenuRow => ({
    id: a.id,
    label: a.label,
    icon: a.icon,
    onSelect: a.onClick,
    disabled: a.disabled,
    active: a.active,
  });
  const leaveHandsetFullscreen = () => {
    if (handsetFullscreen) setHandsetFullscreen(false);
  };
  // NOTIFICATIONS IN FULL SCREEN (2026-10-03, the owner: "how do users see
  // notifications in fullscreen mode? add a badge + menuitem to the ...
  // menu?"; unread-notifications.ts has the why). Full screen takes the
  // account avatar and its unread badge off the page (the rail on a desktop,
  // the bottom bar on a phone), so the bar's ⋯ carries the badge and its
  // menu opens with a Notifications row: the same pair the avatar and the
  // Account panel are, a badge and the first row it leads to. ONLY where the
  // usual badge is not on the page: a phone that still shows its bottom bar
  // gets neither, and a page with no one to notify (`unread` null) gets no
  // row at all.
  const notificationsRow = (onSelect: () => void): AppBarMenuRow => ({
    id: '__notifications__',
    label: t('nav.notifications'),
    icon: Bell,
    onSelect,
    ...(unread
      ? { badge: { text: unreadBadgeText(unread), label: t('workspace.node.unread', { count: unread }) } }
      : {}),
  });
  /** The row after Notifications starts the window's own rows: a hairline over it. */
  const afterNotifications = (rows: AppBarMenuRow[]): AppBarMenuRow[] =>
    rows.map((row, i) => (i === 0 && (!row.kind || row.kind === 'action') ? { ...row, separatorBefore: true } : row));
  const phoneNotifications = handsetUi && handsetFullscreen && unread !== null;
  const phoneMenu: AppBarMenuRow[] = handsetUi
    ? [
        ...(accessoryActions ?? []).map(toRow),
        {
          id: '__handset_fullscreen__',
          label: handsetFullscreen ? t('content.exitFullscreen') : t('workspace.node.fullScreen'),
          icon: handsetFullscreen ? Minimize2 : Maximize2,
          onSelect: () => setHandsetFullscreen(!handsetFullscreen),
        },
        ...(overflowActions ?? []).map(toRow),
        ...(actions ?? []).map(toRow),
        ...(onMinimize
          ? [
              {
                id: '__minimize__',
                label: t('workspace.node.minimize'),
                icon: Minus,
                onSelect: () => {
                  leaveHandsetFullscreen();
                  onMinimize();
                },
              },
            ]
          : showCloseDot
            ? [
                {
                  id: '__dock__',
                  label: t('workspace.node.minimize'),
                  icon: Minus,
                  onSelect: () => {
                    leaveHandsetFullscreen();
                    patchItemState(itemId, { placement: 'docked' });
                  },
                },
              ]
            : []),
        ...(showCloseDot
          ? [
              {
                id: '__close__',
                label: pinnedToDock ? t('workspace.node.sendToDock') : t('workspace.canvas.node.close'),
                icon: X,
                onSelect: () => {
                  leaveHandsetFullscreen();
                  closeWindow();
                },
              },
            ]
          : []),
      ]
    : [];
  // What the phone bar's ⋯ shows: the rows above, under Notifications in
  // full screen. On a phone the notifications view is the sheet the bottom
  // bar's You opens (the left panel's own view), drawn over the app;
  // closing it is back in the full-screen app.
  const phoneBarMenu: AppBarMenuRow[] = phoneNotifications
    ? [
        notificationsRow(() => openWorkspacePanel('left', { tab: 'notifications' })),
        ...afterNotifications(phoneMenu),
      ]
    : phoneMenu;

  // Header layout: inline `actions` → `⋯` overflow menu → universal
  // window controls (maximize/minimize/close), always anchored right.
  const showLayoutPicker =
    !isFullscreen && !!item && isScreenType(item.type);
  const actionStrip = (
    <div className="flex items-center gap-1.5 nodrag nopan ml-auto">
      {(actions ?? []).map((a) => (
        <ActionDot key={a.id} action={a} />
      ))}
      {overflowActions && overflowActions.length > 0 && (
        <OverflowMenu items={overflowActions} />
      )}
      {trailing.map((a) =>
        a.id === '__maximize__' && showLayoutPicker ? (
          <LayoutPickerDot key={a.id} action={a} itemId={itemId} />
        ) : (
          <ActionDot key={a.id} action={a} />
        ),
      )}
    </div>
  );

  // THE ⋯ OF THE DESKTOP FULL-SCREEN APP BAR (lane B; wireframes v3,
  // section B): everything the title bar offered, as a dropdown. The title
  // bar's pills first (the Update pill, then Remix and Drive), then Exit full
  // screen, the window menu's rows (Share, Settings, the publishing rows;
  // its own "Open full screen" is the way IN and gives way to Exit here), the
  // inline dots, Minimize and Close. Minimize and Close leave full screen
  // first, as on a Mac (rule 16: close is exit plus close, minimize is exit
  // plus minimize). Exit is the override (content-node's full-screen toggle)
  // or the takeover's exit, and the bar's own exit button (`onExit`) is the
  // same call; Esc on the page is the canvas's (workspace-canvas.tsx, rule
  // 14), never handled here.
  //
  // A row that opens a dialog portaled to `<body>` (`leavesFullScreen`)
  // used to leave full screen first, because while the NODE was the
  // browser's full-screen element nothing outside it was drawn. Full screen
  // is the page's own view now (the owner, 2026-09-30: the browser is never
  // asked), so those dialogs open in place over the full-screen app; the row
  // still leaves first if some element is ever the browser's full-screen
  // element (`documentFullscreen`: an app's video, the person's own
  // browser full screen of a single element), where they would be unseen.
  const leaveFullScreen = () => {
    if (maximizeOverrideActive) onMaximizeOverride?.();
    else if (isFullscreen) exitFullscreen();
  };
  const toFullScreenRow = (a: NodeFrameAction): AppBarMenuRow => ({
    id: a.id,
    label: a.label,
    icon: a.icon,
    disabled: a.disabled,
    active: a.active,
    onSelect:
      a.leavesFullScreen && maximizeOverrideActive && documentFullscreen
        ? () => {
            leaveFullScreen();
            a.onClick();
          }
        : a.onClick,
  });
  // Notifications (see NOTIFICATIONS IN FULL SCREEN above). In the page's
  // own full screen the card opens OVER the app, under ⋯ (the top panel's
  // top-right card, 60 px down: 16 px under this 44 px bar). The takeover
  // is a fixed layer above every panel, and an element the browser presents
  // full screen hides everything but itself, so there the row leaves first
  // and opens the view where it lives outside full screen.
  const desktopNotifications = desktopFullScreen && unread !== null;
  const openDesktopNotifications = () => {
    if (maximizeOverrideActive && !documentFullscreen) {
      openWorkspacePanel('top');
      return;
    }
    leaveFullScreen();
    openWorkspacePanel('left', { tab: 'notifications' });
  };
  const fullScreenRows: AppBarMenuRow[] = desktopFullScreen
    ? [
        ...(desktopNotifications ? [notificationsRow(openDesktopNotifications)] : []),
        ...(fullScreenActions ?? []).map(toFullScreenRow),
        ...(accessoryActions ?? []).map(toFullScreenRow),
        {
          id: '__exit_fullscreen__',
          label: t('content.exitFullscreen'),
          icon: Minimize2,
          onSelect: leaveFullScreen,
        },
        ...(overflowActions ?? []).filter((a) => a.id !== 'open-fullscreen').map(toFullScreenRow),
        ...(actions ?? []).map(toFullScreenRow),
        ...(onMinimize
          ? [
              {
                id: '__minimize__',
                label: t('workspace.node.minimize'),
                icon: Minus,
                onSelect: () => {
                  leaveFullScreen();
                  onMinimize();
                },
              },
            ]
          : showCloseDot
            ? [
                {
                  id: '__dock__',
                  label: t('workspace.node.minimize'),
                  icon: Minus,
                  onSelect: () => {
                    leaveFullScreen();
                    patchItemState(itemId, { placement: 'docked' });
                  },
                },
              ]
            : []),
        ...(showCloseDot
          ? [
              {
                id: '__close__',
                label: pinnedToDock ? t('workspace.node.sendToDock') : t('workspace.canvas.node.close'),
                icon: X,
                onSelect: () => {
                  leaveFullScreen();
                  closeWindow();
                },
              },
            ]
          : []),
      ]
    : [];
  // Three hairlines group the dropdown (the design pass of 2026-09-30 found
  // seven undivided rows): the app's own pills, then Exit full screen, then
  // the window menu's rows, then Minimize and Close. Notifications, when it
  // is there, is a group of its own above them all.
  const fullScreenMenu: AppBarMenuRow[] = fullScreenRows.map((row, i) => {
    if (row.kind && row.kind !== 'action') return row;
    const prev = fullScreenRows[i - 1];
    const startsGroup =
      prev?.id === '__notifications__' ||
      row.id === '__exit_fullscreen__' ||
      prev?.id === '__exit_fullscreen__' ||
      ((row.id === '__minimize__' || row.id === '__dock__' || row.id === '__close__') &&
        prev?.id !== '__minimize__' &&
        prev?.id !== '__dock__');
    return startsGroup ? { ...row, separatorBefore: true } : row;
  });

  // Corner badge is fixed in place — top-left corner of the node — in
  // BOTH expanded and collapsed states. Keeping it stationary is the
  // whole point of the collapse animation: only the frame (chrome +
  // body) fades out, and a name-under-avatar label fades in beneath
  // the still-anchored avatar.
  //
  // Avatar is a drag handle in BOTH states. A click without movement
  // still toggles collapse — React Flow's drag threshold (a few
  // pixels) cleanly separates tap from drag, so the button's onClick
  // only fires for taps. This lets the user reposition the widget by
  // grabbing the most prominent part of it — the avatar — without
  // having to aim for the narrow header strip beside the avatar.
  // Collapsed mode shrinks the React Flow bbox to wrap the avatar
  // exactly (see agent-node's `toggleCollapsed` — 64×64). Inside
  // that bbox the avatar fills the whole space (no negative offset)
  // so the four cardinal connector handles land flush against the
  // avatar's ring. The full-frame layout still uses the historic
  // top-left offset so the avatar overhangs the card chrome.
  const cornerBadgeNode = cornerBadge && (
    <div
      className={cn(
        'absolute z-20 ws-node-drag-handle cursor-grab active:cursor-grabbing',
        collapsed
          // Top-aligned 64x64 band — leaves the bottom 28px of the
          // 64x92 collapsed bbox for the under-avatar @username
          // label and keeps the left/right cardinal handles sitting
          // on the avatar mid-line (CSS overrides them to top:32px
          // in collapsed mode; see globals.css).
          ? 'top-0 left-0 right-0 h-16 flex items-center justify-center'
          : '-left-1 -top-2 -translate-x-2 translate-y-0',
      )}
    >
      {onCornerBadgeClick ? (
        <button
          type="button"
          onClick={(e) => {
            // Prevent the click from bubbling to the drag handle or
            // canvas pan logic — we only want the collapse toggle.
            e.stopPropagation();
            onCornerBadgeClick();
          }}
          aria-label={cornerBadgeAriaLabel ?? 'Toggle widget'}
          aria-expanded={!collapsed}
          className="relative block rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring/60 transition-transform hover:scale-105 active:scale-95 cursor-grab active:cursor-grabbing"
        >
          {cornerBadge}
          {/*
            Under-avatar label — appears only in collapsed mode and is
            centred on the avatar's horizontal axis via `left-1/2 +
            -translate-x-1/2`. Width is wider than the avatar (160px)
            so longer agent names / handles don't get clipped to the
            avatar's 56px column; both lines truncate with ellipsis
            past that. `top-full` puts it directly below the avatar
            with a small `mt-1` gap.
          */}
          <span
            className={cn(
              'absolute left-1/2 top-full mt-1 -translate-x-1/2 w-40 text-center pointer-events-none transition-opacity duration-300 ease-out',
              collapsed ? 'opacity-100' : 'opacity-0',
            )}
            aria-hidden={!collapsed}
          >
            <span className="block truncate text-[12px] font-semibold leading-tight text-foreground drop-shadow-sm">
              {title}
            </span>
            {subtitle && (
              <span className="block truncate text-[11px] leading-tight text-muted-foreground">
                {subtitle}
              </span>
            )}
          </span>
        </button>
      ) : (
        cornerBadge
      )}
    </div>
  );

  // ---- context menu plumbing -------------------------------------------
  // Right-click on the frame dispatches a window event the canvas
  // listens for, so the canvas can render a single shared menu portal
  // instead of one per widget. We rely on ReactFlow's own
  // `onNodeContextMenu` for mouse-driven right-clicks (it fires before
  // the browser default and we preventDefault from there); this
  // handler covers the touch long-press case ReactFlow doesn't
  // emit. Mirrors the file-manager long-press contract — 500ms hold,
  // cancel on >8px move.
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressStart = useRef<{ x: number; y: number } | null>(null);
  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    longPressStart.current = null;
  };

  // Esc no longer exits fullscreen directly — widgets consume Esc
  // locally (palettes, deselect, cancel), so an instant exit double-fired
  // and never worked while an iframe had focus anyway. The Esc-HOLD
  // gesture (fsChrome above) reveals the header instead; exit lives on
  // the header's green window control.

  /*
    Fullscreen takeover — implementation note.

    When `isFullscreen` is true we render the frame layer through
    `createPortal(document.body)` instead of leaving it inside the
    `.react-flow__node` wrapper. This is deliberately the SAME React
    element either way, just teleported to a different DOM parent —
    React preserves component identity across the portal, so internal
    widget state (chat scroll, draft text, codemirror cursor, etc.) is
    not lost when toggling.

    Why not a CSS-only `fixed inset-0` on the in-place node?
      - React Flow's `.react-flow__node` wrapper retains its inline
        `width / height` set by RF based on store state. Reparenting
        the frame layer keeps that wrapper intact, so RF's
        ResizeObserver never sees a dimension change and never writes
        a viewport-sized width back into the store (previous bug:
        widgets stayed huge after exit).
      - `.react-flow__viewport` creates its own stacking context via
        `transform`. A `z-index: 200` on a descendant is scoped to
        that context, so workspace chrome at `z-30` painted ON TOP
        of "fullscreen". Portaling to `document.body` escapes every
        ancestor stacking context cleanly.

    The wrapper that stays in the RF tree (`<div className="ws-node-
    frame …">`) becomes a 0-height husk while the portal is active
    (resize edges + corner badge are conditionally rendered, frame
    layer is portaled away). It still sits at the original node
    position, hidden behind the fullscreen portal — no chrome bleed.
  */
  // Header content shared between the two header placements: the normal
  // in-flow chrome row, and the fullscreen overlay bar (Esc-hold reveal).
  // Same title / accessory / action strip either way — the fullscreen bar
  // is the REAL app header, not a copy with fewer controls.
  const headerContent = (
    <>
      {/* Specular highlight + chromatic refraction wash. Pointer-
          events disabled so it never intercepts drag/clicks. */}
      <span aria-hidden className="ws-node-header-shine pointer-events-none absolute inset-0" />
      <span aria-hidden className="ws-node-header-refract pointer-events-none absolute inset-0" />

      {/* The title keeps its natural width (`flex: 0 1 auto`): it shrinks
          only when the chrome itself no longer fits the window, exactly as
          before, and is never shortened to make room for an ad. */}
      <div className="relative z-[1] min-w-0 flex flex-col leading-tight">
        <div className="min-w-0 truncate text-[13px] font-semibold text-foreground">
          {title}
        </div>
        {subtitle && (
          <div className="min-w-0 truncate text-[11px] text-muted-foreground">
            {subtitle}
          </div>
        )}
      </div>

      {/* The ad slot owns the free middle (`min-w-0 flex-1`, ads build plan
          section 2.3), so it takes the space the title used to stretch into
          and filling or emptying it moves nothing. Its own padding plus the
          row's gap keep 48 px between an ad and the title, and between an ad
          and the pills. One line of 11 px text, shorter than the pills, and
          no height of its own when empty, so the bar keeps its height. */}
      <AdSlot placement="titleBar" appId={adAppId} active={adActive} />

      {headerAccessory && (
        // Flex row, not a plain block: the accessory can be several pills
        // (Update + Remix/Drive), and the pair's `display:flex` span is
        // block-level — in a block wrapper it dropped onto a second line
        // whenever the Update CTA rendered next to it.
        <div className="relative z-[1] shrink-0 nodrag nopan flex items-center">{headerAccessory}</div>
      )}

      <div className="relative z-[1] shrink-0">{actionStrip}</div>
    </>
  );

  const frameLayer = (
    <div
      ref={fsChrome.containerRef}
      className={cn(
        'overflow-hidden flex flex-col transition-opacity duration-300 ease-out',
        isFullscreen
          // Portaled to <body>: take over the viewport directly, drop
          // the rounded corners + glass body so the widget renders
          // edge to edge with an opaque backdrop behind any
          // translucent regions.
          ? 'fixed inset-0 z-[200] rounded-none bg-background'
          : handsetUi
            // The phone app view: edge to edge, no rounded corners, no
            // glass card and no shadow (handoff section 4.2). The layer is
            // TRANSPARENT and the body below carries the background, so
            // the app bar's glass sees the canvas behind the node, not an
            // opaque card.
            ? 'absolute inset-0'
          : maximizeOverrideActive
            // Desktop full screen in place (lane B): the node IS the
            // browser's full-screen element, so the layer is edge to edge
            // and square, and opaque: behind a transparent layer the glass
            // bar would show the browser's black backdrop, not the page.
            ? 'absolute inset-0 rounded-none bg-background'
          : item?.state?.hideHeader
            // Headerless mode (e.g. pasted images / videos): drop
            // the entire glass card — no white background, no
            // rounded corners, no rim shadow. The body fills the
            // node bounds edge-to-edge so transparent PNGs / SVGs
            // composite directly against the canvas dotted grid.
            // We keep `overflow-hidden` so an `object-contain` /
            // `object-cover` swap downstream can't visually
            // overflow the resize edges.
            ? 'absolute inset-0'
            : 'absolute inset-0 rounded-2xl glass-strong',
        collapsedUi ? 'opacity-0 pointer-events-none' : 'opacity-100',
      )}
      aria-label={titleText}
      aria-hidden={collapsedUi}
    >
      {/*
        Harmonized header — single design across all widget types.
        - Glass surface with two stacked refraction overlays:
            (a) a soft top→bottom highlight that mimics Apple's
                "specular" glass shine, and
            (b) a subtle radial chromatic wash on the left to suggest
                a light source bending through the surface.
        - Drag handle (`.ws-node-drag-handle`) so React Flow only
          drags when the user grabs the chrome, not the body.
        - Left padding is generous (`pl-12`) when a cornerBadge is
          present so the floating avatar has room to peek out the
          top-left corner without overlapping the title text.
        - Right: action strip (`...`, then maximize / minimize /
          close trio pinned to the right edge).
      */}
      {/* The phone app bar (handoff section 5, node-frame row): the one
          shared glass header, IN FLOW above the body so it never lies over
          the app. No `ws-node-drag-handle`, no double-tap: the canvas draws
          this node `draggable: false` and the bar is not a handle. The
          sideways swipe pages between apps through the same `ws:screen`
          chokepoint as the retired edge strips. */}
      {!isFullscreen && handsetUi && (
        <AppBar
          variant="phone"
          icon={item ? identityVisual(item, 24, live) : null}
          name={title}
          nameText={titleText}
          placement="appBar"
          appId={adAppId}
          adActive={appBarAdActive}
          menu={phoneBarMenu}
          unread={phoneNotifications ? unread : null}
          onSwipe={(dir) =>
            window.dispatchEvent(
              new CustomEvent('ws:screen', { detail: { kind: dir === 1 ? 'next' : 'prev' } }),
            )
          }
        />
      )}
      {/* The desktop full-screen app bar (lane B; see `desktopFullScreen`):
          the same shared bar, variant `desktop` (⋯ is a dropdown, the ad
          centres in the free middle and stops at 470 px), placement
          `fullScreen`, IN FLOW above the body, so the app's box starts under
          its hairline and nothing of the app is ever covered. Not a handle:
          no drag class, no double-click, no swipe. */}
      {desktopFullScreen && (
        <AppBar
          variant="desktop"
          testId="fullscreen-app-bar"
          icon={item ? identityVisual(item, 24, live) : null}
          name={title}
          nameText={titleText}
          placement="fullScreen"
          appId={adAppId}
          adActive={fullScreenAdActive}
          menu={fullScreenMenu}
          onExit={leaveFullScreen}
          unread={desktopNotifications ? unread : null}
        />
      )}
      {!isFullscreen && !handsetUi && (
        <div
          className={cn(
            // `z-10` lifts the header (and the `⋯` overflow popover, which
            // drops `top-full` into the body) above the body region. Iframe
            // widgets wrap their bridge in a `position: relative` box
            // (content-node's iframe branch) — a positioned sibling that, at
            // `z-auto`, paints AFTER the header in tree order and would
            // otherwise occlude the popover where it overlaps the body, so the
            // menu opened but rendered behind the widget iframe ("clicking ⋯
            // does nothing"). The header strip never overlaps body content
            // spatially, so out-ranking it here is safe. Scoped per-frame.
            // A fixed 40 px bar (`h-10`), not `py-2` around whatever it holds:
            // with the pills it measured 42.5 px, so every app's body began
            // on a half pixel, and a bar without pills was shorter than one
            // with them (the design pass of 2026-09-30). 40 is the number
            // the ad slot and the wireframes were drawn for.
            'ws-node-drag-handle ws-node-header relative z-10 shrink-0 flex h-10 items-center gap-2 pr-3 select-none cursor-grab active:cursor-grabbing',
            cornerBadge ? 'pl-14' : 'pl-3',
            // Per-node "hide widget title" preference. Used for
            // pasted media (image / video) where the body conveys
            // the identity by itself and the chrome title reads as
            // duplicate noise. The body wrapper below also picks
            // this up to drop the top padding so the body fills
            // the rectangle edge-to-edge without a chrome-shaped
            // hole at the top.
            item?.state?.hideHeader && 'hidden',
            // Desktop full screen in place (lane B): the app bar above is
            // the header, so the title bar stands down the same way. Its
            // ad slot is inactive there (`adActive`), so nothing is fetched.
            maximizeOverrideActive && 'hidden',
          )}
          title={titleText}
          // Header double-click. Desktop: focus the camera on this widget
          // (zoom + pan to fit). Touch: STRETCH the window to fill the
          // screen — the phone counterpart of maximize, via the same
          // layout chokepoint the tiling uses. Scoped to the header so a
          // double-click inside a chat input or editor body never
          // triggers either.
          onDoubleClick={(e) => {
            e.stopPropagation();
            if (coarseHeaderGestures) {
              window.dispatchEvent(
                new CustomEvent('ws:screen', {
                  detail: { kind: 'layout', itemId, layout: 'full' },
                }),
              );
              return;
            }
            if (onFocus) onFocus();
          }}
        >
          {headerContent}
        </div>
      )}

      {/*
        Body region.

        Normal mode — `nodrag` keeps pointer interactions on
        widget content from triggering the canvas drag, and the
        chrome header above owns the drag-handle hotspot.

        Headerless mode (per-node `hideHeader`, e.g. pasted
        images / videos) — promote the entire body to the
        `.ws-node-drag-handle` so the user can grab + drag the
        widget from anywhere on the picture / video frame. There's
        no chrome to act as the drag affordance, so the body IS
        the affordance. We deliberately drop `nodrag` in this
        mode so React Flow's pointerdown reaches the body, and we
        show the grab cursor on hover so the gesture is
        discoverable.

        We deliberately do NOT add `nowheel`: that would trap
        Cmd/trackpad scroll-zoom and pinch-zoom inside the
        widget. Components that own the wheel (CodeMirror, chats
        list, scrolling settings pane) opt in locally.
      */}
      <div
        className={cn(
          'flex-1 min-h-0 overflow-hidden',
          item?.state?.hideHeader && !isFullscreen && !handsetUi
            ? 'ws-node-drag-handle cursor-grab active:cursor-grabbing'
            : 'nodrag',
          // Fullscreen drops the body padding so the widget gets
          // every pixel of the viewport — chats / editors render
          // edge to edge as if they owned the window. So does the phone
          // app view, which also carries the background here (the frame
          // layer is transparent under the glass bar).
          bodyPad && !isFullscreen && !handsetUi ? 'p-2.5' : '',
          handsetUi && 'bg-background',
          contentClassName,
        )}
      >
        {children}
      </div>

      {/* Overview: cover the app so the tiny iframe can't eat clicks;
          click = fly in, drag = rearrange (see OverviewCover above). */}
      {showOverviewCover && <OverviewCover itemId={itemId} title={titleText} />}

      {/*
        Fullscreen chrome — the app owns the full viewport with zero
        chrome at rest; holding Esc (progress pill), pushing the mouse to
        the top edge, or tapping the touch grab handle slides the REAL
        app header in as an overlay: title, Remix/Drive accessory, the ⋯
        menu and the window-control trio (whose green dot reads "Exit
        fullscreen" here). Dialogs those controls open portal to <body>,
        outside this container, so they paint above the takeover and
        never dismiss the header. See fullscreen-chrome.tsx.

        A PHONE takeover only since lane B (2026-09-29): on a desktop the
        takeover's header is the always-visible app bar above, and this
        pull-down with its hover zone, handle and "Hold Esc" hint is gone
        from that path.
      */}
      {isFullscreen && handsetUi && (
        <FullscreenChrome chrome={fsChrome} testId="fullscreen-header">
          <div
            className="ws-node-header liquid-glass ws-panel-solid relative flex items-center gap-2 py-2 pl-3 pr-3 shadow-lg select-none"
            title={titleText}
          >
            {headerContent}
          </div>
        </FullscreenChrome>
      )}
    </div>
  );

  return (
    <div
      className={cn(
        'ws-node-frame group relative h-full w-full flex flex-col',
        // Headerless nodes (pasted images / videos) drop the
        // outer rounded clip too, so the picture's own edges are
        // the silhouette. Selection chrome + resize edges still
        // anchor to the same rectangle. The phone app view is square:
        // edge to edge, no corners.
        item?.state?.hideHeader || handsetUi ? '' : 'rounded-2xl',
      )}
      data-handset-presented={handsetUi && presentedId === itemId ? 'true' : undefined}
      // Stable selector for E2E smoke (Phase 7 of workspace-only
      // navigation) — every NodeFrame instance becomes a
      // queryable canvas node so we can assert the spawn after the
      // preview's "Add to workspace" CTA without depending on
      // ReactFlow's internal class names.
      data-testid="workspace-node"
      // Additional facets used by `tests/e2e/**`. The smoke flow
      // only needs `data-testid="workspace-node"` for the spawn-
      // count assertion, but the broader e2e suite filters by
      // type / username (e.g. "find the @robutler.factory chat
      // composer") — exposing the facets here means the filters
      // stay stable across React Flow upgrades and the per-node
      // file refactors.
      data-item-id={itemId}
      data-item-type={item?.type ?? undefined}
      data-item-ref-id={item?.refId ?? undefined}
      data-node-username={
        typeof item?.state?.cache?.username === 'string'
          ? (item.state.cache.username as string)
          : undefined
      }
      // CSS hook: collapsed-mode handle overrides live in
      // globals.css and key off this attribute (see the
      // `.ws-node-frame[data-collapsed='true'] .ws-conn-handle-l/r`
      // block). Using `undefined` instead of `'false'` so the
      // attribute is fully absent when expanded, keeping the
      // attribute selector specificity minimal.
      data-collapsed={collapsedUi ? 'true' : undefined}
      // Suppress the browser's native contextmenu at the deepest target.
      // The capture-phase listener on `WorkspaceCanvas` does the same thing
      // already, but layered defense is cheap: if a descendant calls
      // `stopPropagation()` on the contextmenu event (CodeMirror, chat
      // scroller, contenteditable annotations all do this), the capture
      // handler never sees it. Without preventDefault at the node level,
      // the user gets the browser's native menu stacked on top of our
      // own — exactly the bug we just fixed for long-press on touch.
      onContextMenu={(e) => e.preventDefault()}
      onPointerDown={(e) => {
        // Skip when the pointer landed on a React Flow connection
        // handle. RF v12 takes a small but non-zero amount of work
        // to start the connect gesture, and any synchronous re-
        // render (here: `bringToFront` patches `state.zIndex`)
        // that races against `<Handle>`'s internal state-machine
        // can flush the pending connection drag — the user
        // observes "I clicked the handle but no line appeared,
        // and the image now sits on top of everything else".
        // The handle's own pointerdown already brings the node
        // forward via RF's selection lift, so deferring is safe.
        //
        // Same gate skips the touch long-press timer for handle
        // taps: the long-press menu fires `node-context-menu` at
        // 500 ms and would shadow a slow finger drag from a
        // cardinal connector dot.
        const target = e.target as HTMLElement | null;
        if (target?.closest('.react-flow__handle')) return;
        // A handset raises nothing (z-order is shared; the phone presents
        // one window) and has no long-press item menu: RV-034 was a short
        // drag opening that menu with Delete one tap away.
        if (handsetUi) return;
        bringToFront(itemId);
        if (e.pointerType !== 'mouse') {
          longPressStart.current = { x: e.clientX, y: e.clientY };
          longPressTimer.current = setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent('workspace:node-context-menu', {
                detail: { itemId, x: e.clientX, y: e.clientY },
              }),
            );
            longPressTimer.current = null;
          }, 500);
        }
      }}
      onPointerMove={(e) => {
        const start = longPressStart.current;
        if (!start || !longPressTimer.current) return;
        const dx = e.clientX - start.x;
        const dy = e.clientY - start.y;
        if (dx * dx + dy * dy > 64) cancelLongPress();
      }}
      onPointerUp={cancelLongPress}
      onPointerCancel={cancelLongPress}
      // Double-click / double-tap anywhere on the widget focuses
      // the camera on it (zoom + pan to fit). The header already
      // has its own handler with `stopPropagation`, so a header
      // dblclick is consumed there and never bubbles up here. We
      // additionally bail out when the gesture lands inside an
      // interactive surface — chat inputs, CodeMirror, native
      // form controls — where a double-click means "select word"
      // or some other content-local action and a viewport jump
      // would feel hostile.
      onDoubleClick={(e) => {
        // No double-tap on a handset: a content node's `onFocus` is the
        // window-manager maximize, which WRITES geometry for every device.
        // Nor in desktop full screen in place (lane B): a double-click on
        // the app bar would resize the stored window for everyone while the
        // person is looking at the full-screen app.
        if (maximizeOverrideActive) return;
        if (!onFocus || isFullscreen || handsetUi) return;
        const t = e.target as HTMLElement | null;
        if (
          t?.closest(
            'input, textarea, select, button, a, [contenteditable="true"], .cm-editor, .ProseMirror',
          )
        ) {
          return;
        }
        e.stopPropagation();
        onFocus();
      }}
    >
      {/*
        Resize affordances — invisible edge / corner hit areas on every
        viewport. No frame, no handles, no chrome of any kind: resizing
        feels like pulling the card itself. Skipped while `collapsed`
        or `isFullscreen` so the gesture surface vanishes when the
        widget isn't presented as a resizable window.
      */}
      {!collapsedUi && !isFullscreen && !handsetUi && (
        <>
          <ResizeEdge
            position="t"
            onPointerDown={(e) => beginResize(e, 't')}
            onPointerMove={onResizeMove}
            onPointerUp={endResize}
            onPointerCancel={endResize}
          />
          <ResizeEdge
            position="tl"
            onPointerDown={(e) => beginResize(e, 'tl')}
            onPointerMove={onResizeMove}
            onPointerUp={endResize}
            onPointerCancel={endResize}
          />
          <ResizeEdge
            position="tr"
            onPointerDown={(e) => beginResize(e, 'tr')}
            onPointerMove={onResizeMove}
            onPointerUp={endResize}
            onPointerCancel={endResize}
          />
          <ResizeEdge
            position="l"
            onPointerDown={(e) => beginResize(e, 'l')}
            onPointerMove={onResizeMove}
            onPointerUp={endResize}
            onPointerCancel={endResize}
          />
          <ResizeEdge
            position="r"
            onPointerDown={(e) => beginResize(e, 'r')}
            onPointerMove={onResizeMove}
            onPointerUp={endResize}
            onPointerCancel={endResize}
          />
          <ResizeEdge
            position="b"
            onPointerDown={(e) => beginResize(e, 'b')}
            onPointerMove={onResizeMove}
            onPointerUp={endResize}
            onPointerCancel={endResize}
          />
          <ResizeEdge
            position="bl"
            onPointerDown={(e) => beginResize(e, 'bl')}
            onPointerMove={onResizeMove}
            onPointerUp={endResize}
            onPointerCancel={endResize}
          />
          <ResizeEdge
            position="br"
            onPointerDown={(e) => beginResize(e, 'br')}
            onPointerMove={onResizeMove}
            onPointerUp={endResize}
            onPointerCancel={endResize}
          />
        </>
      )}

      {/* The corner badge overhangs the top-left corner; on a phone the
          app bar carries the same identity as its icon instead. */}
      {!isFullscreen && !handsetUi && cornerBadgeNode}

      {/* Frame layer — portaled to <body> while fullscreen, see
          comment above. SSR-safe guard: we only portal once the
          document object exists (this component is "use client" so
          it only renders on the client anyway, but defensive). */}
      {isFullscreen && typeof document !== 'undefined'
        ? createPortal(frameLayer, document.body)
        : frameLayer}

      {/*
        Connection handles for the connector tool. Four cardinal
        handles (top / right / bottom / left). Each handle is typed
        `source` with `isConnectableEnd` so it can be either end of
        a connection — RF v12 lets a single source handle act as
        both start and end, which keeps the click target large and
        unambiguous (no overlapping source+target stacks to land
        between). The handle dots are invisible by default; styling
        and the visibility ramp (default → hover → connector-tool)
        live in globals.css next to `.ws-conn-handle`.

        Handles must stay mounted (just opacity-hidden) so React
        Flow can measure their layout box — `display: none` would
        give them a zero rect and silently break `onConnect`.
        Collapsed mode keeps them mounted too: the bbox is shrunken
        to wrap the avatar (see `cornerBadgeNode` + agent-node's
        `toggleCollapsed`) so the handles sit flush on the avatar
        ring and connectors stay attached when the widget folds
        into a puck. Without that we'd lose every edge anchor the
        moment the user collapsed the chat.

        DOM-order matters: handles render AFTER `frameLayer` so
        they paint above the body in browsers where the global
        `z-index: 40` on `.ws-conn-handle` isn't enough to outrank
        an opaque body that's already established a paint context
        (e.g. via `transform`, `filter`, or `will-change`). Pasted
        images / videos run in headerless mode where the body
        spans edge-to-edge with no chrome between it and the
        handle dots, so any z-stack ambiguity translated directly
        to a "can't grab a connector off a picture" symptom the
        user flagged. Mirrors the sticky / shape annotation
        layout order which doesn't exhibit the bug.
      */}
      {!isFullscreen && (
        <>
          <Handle
            id="t"
            type="source"
            position={Position.Top}
            className="ws-conn-handle ws-conn-handle-t"
            isConnectableEnd
          />
          <Handle
            id="r"
            type="source"
            position={Position.Right}
            className="ws-conn-handle ws-conn-handle-r"
            isConnectableEnd
          />
          <Handle
            id="b"
            type="source"
            position={Position.Bottom}
            className="ws-conn-handle ws-conn-handle-b"
            isConnectableEnd
          />
          <Handle
            id="l"
            type="source"
            position={Position.Left}
            className="ws-conn-handle ws-conn-handle-l"
            isConnectableEnd
          />
        </>
      )}
    </div>
  );
}

type EdgePos = 't' | 'l' | 'r' | 'b' | 'tl' | 'tr' | 'bl' | 'br';

/**
 * Invisible edge / corner hit area for desktop node resize.
 *
 * Each handle sits on the outside of the card so the actual card border
 * isn't visually disrupted by a chrome ring. We deliberately extend
 * each handle a few px BEYOND the card edge — that way the cursor
 * picks up `*-resize` slightly before the user touches the visible
 * edge, which feels precise without forcing pixel-perfect aim.
 *
 * Class `nodrag nopan` (in addition to `stopPropagation` in the
 * handler) keeps the React Flow background pan from stealing the
 * gesture while the user is mid-resize. Corner handles are placed on
 * top of the edge handles via z-index so a click at a corner doesn't
 * land on the wrong edge.
 */
function ResizeEdge({
  position,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
}: {
  position: EdgePos;
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
}) {
  const t = useT();
  const isCorner =
    position === 'tl' ||
    position === 'tr' ||
    position === 'bl' ||
    position === 'br';
  return (
    <div
      role="separator"
      aria-orientation={
        position === 'b' || position === 't' ? 'horizontal' : 'vertical'
      }
      aria-label={t('workspace.node.resize')}
      data-edge={position}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      className={cn(
        // `ws-resize-edge` is the shared marker CSS keys off to disable
        // these hit zones when a tool is armed and — on touch — on
        // unselected widgets (see globals.css). The shared
        // `components/workspace/resize-edge.tsx` carries it too.
        'ws-resize-edge nodrag nopan absolute pointer-events-auto',
        // Corners win the z stack so they're not eaten by adjacent edges.
        isCorner ? 'z-30' : 'z-20',
        // Hit area generous enough for touch (≥24px corners) while
        // edges stay thin since they get the full card length. The TOP
        // strip leans OUTWARD (-top-2, only 1px inside) so the header
        // keeps almost its full height as a drag surface — hovering the
        // very edge resizes, everything below it moves the window,
        // exactly like an OS title bar.
        //
        // THE RIGHT AND BOTTOM EDGES AND THE FOUR CORNERS REACH AT MOST 4 PX
        // PAST THE WINDOW (2026-09-29, lane D; the Stage algorithm, rule 4
        // and "The resize edges must leave the strip alone"). A new window
        // sticks out 12 px past the one it covers, and a press on a resize
        // strip resizes and raises the window that OWNS the strip. At the old
        // 6 px (edges) and 8 px (corners) only 6 px of a covered window's 12 px
        // strip, and 4 px beside the corner, still brought it back; at 4 px
        // at least 8 px stay the covered window's. The grab areas keep their
        // size (12 px strips, 24 px corners) and lean inward instead. The
        // top edge keeps its outward reach for the title bar's drag surface,
        // and the left edge its 6 px: the strip a covered window keeps there
        // is 28 px wide. Pinned by tests/unit/workspaces/stage-lane-d-guards.test.ts.
        position === 't' && 'left-2 right-2 -top-2 h-3 cursor-ns-resize',
        position === 'l' && 'top-0 bottom-0 -left-1.5 w-3 cursor-ew-resize',
        position === 'r' && 'top-0 bottom-0 -right-1 w-3 cursor-ew-resize',
        position === 'b' && 'left-0 right-0 -bottom-1 h-3 cursor-ns-resize',
        position === 'tl' && '-left-1 -top-1 h-6 w-6 cursor-nwse-resize',
        position === 'tr' && '-right-1 -top-1 h-6 w-6 cursor-nesw-resize',
        position === 'bl' && '-left-1 -bottom-1 h-6 w-6 cursor-nesw-resize',
        position === 'br' && '-right-1 -bottom-1 h-6 w-6 cursor-nwse-resize',
      )}
    />
  );
}
